import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useTypedHash, useTypedParams } from 'react-router-typesafe-routes';
import { type GridComponents, type GridListProps, VirtuosoGrid } from 'react-virtuoso';
import MediaPreviewContent from '@/components/common/media-content/media-preview-content';
import TabList from '@/components/common/tablist';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useMediaMessages, { type MediaMessageFilterReducerActionType } from '@/hooks/conversations/use-media-messages';
import useScrollRestoration from '@/hooks/use-scroll-restoration';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import cn from '@/utils/merge-class-names';

interface Props {
  type: ConversationType;
}

const gridComponents: GridComponents = {
  List: forwardRef<HTMLDivElement, GridListProps>(({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      {...props}
      className={cn('grid grid-cols-2 md:grid-cols-3 auto-rows-fr gap-2 mx-5', className)}>
      {children}
    </div>
  )),
  Item: ({ children, className, ...props }) => (
    <div
      {...props}
      className={cn('break-all overflow-hidden aspect-square', className)}>
      {children}
    </div>
  ),
};

export default function MediaMessagesIndexRoute({ type }: Props) {
  useScrollRestoration();
  const { t } = useTranslation();
  const { id } = useTypedParams(ROUTES.MESSAGES.SESSIONS.MEDIA || ROUTES.MESSAGES.GROUPS.MEDIA);
  const hash = useTypedHash(ROUTES.MESSAGES.SESSIONS.MEDIA || ROUTES.MESSAGES.GROUPS.MEDIA);
  const { messages, filteredBy, setFilter } = useMediaMessages(undefined, hash);

  const handleChangeFilter = (filterType: MediaMessageFilterReducerActionType) => {
    setFilter(filterType);
    const href = (type === 'session' ? ROUTES.MESSAGES.SESSIONS.MEDIA : ROUTES.MESSAGES.GROUPS.MEDIA).$buildPath({
      params: { id },
      hash: filterType === 'all' ? undefined : filterType,
    });

    history.replaceState(null, '', href);
  };

  return (
    <PageLayout
      headerConfig={{
        navigateBack: true,
        elementAfterNav: (
          <TabList
            tabs={filterTabs}
            defaultActiveTab={filteredBy}
            onClickTab={handleChangeFilter}
            className='bg-white mt-2'
          />
        ),
      }}
      title={t('conversation.media.title')}>
      <VirtuosoGrid
        data={messages}
        className='flex flex-1'
        components={gridComponents}
        itemContent={(_, m) => (
          <Link
            to={(type === 'session'
              ? ROUTES.MESSAGES.SESSIONS.MEDIA.$.BY_ID
              : ROUTES.MESSAGES.GROUPS.MEDIA.$.BY_ID
            ).$buildPath({
              relative: true,
              params: {
                messageId: m.ID,
              },
            })}
            state={{ from: 'index' }}>
            <MediaPreviewContent
              type={m.MsgType}
              src={m.Message}
              className='desktop:w-[10.5rem] desktop:h-[10.5rem]'
            />
          </Link>
        )}
      />
    </PageLayout>
  );
}

const filterTabs: Array<{ key: MediaMessageFilterReducerActionType; label: string }> = [
  {
    key: 'all',
    label: 'conversation.media.filters.all',
  },
  {
    key: 'images',
    label: 'conversation.media.filters.images',
  },
  {
    key: 'videos',
    label: 'conversation.media.filters.videos',
  },
  {
    key: 'files',
    label: 'conversation.media.filters.files',
  },
];
